import type { TFunction } from 'i18next';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { etykietaObszaru, parseArea } from '@/lib/areaSearch';
import { DEFAULT_FILTERS, filtersToParams, paramsToFilters } from '@/lib/serpUrl';
import { etykietaCenyZ } from '@/lib/serpFormat';
import { etykietaWyboruPokoi } from '@/lib/pokoje';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';

/**
 * HISTORIA WYSZUKIWAŃ (wzór qasa „Recent searches", zlecenie 28.09).
 *
 * WPIS = PEŁNY STAN WYSZUKIWANIA — dokładnie to, co serializuje adres (`filtersToParams`):
 * obszary, tryb i zakres ceny, pokoje, metraż, pozostałe filtry, sortowanie. Bez widoku
 * (lista/mapa) i bez kadru mapy — to nie jest „czego szukałem", tylko „jak patrzyłem".
 * Jeden format z adresem, żeby nie powstał drugi, równoległy zapis tego samego stanu.
 *
 * ZAPIS PRZY ZATWIERDZENIU (wybór obszaru, „Pokaż N ofert", otwarcie oferty z wyników), nie
 * przy każdym ruchu suwaka. Ten sam stan przesuwa się na górę zamiast się dublować; najwyżej 5.
 * Pusty stan (miasto bez filtrów) nie jest zapisywany — nie ma czego przywracać.
 *
 * PRZECHOWYWANIE: `localStorage` tej przeglądarki, każdy odczyt i zapis w try/catch (okno
 * prywatne i zablokowane dane witryny potrafią RZUCIĆ). Klucz wersjonowany. Kształt wpisu
 * (`{ q, t }`) jest gotowy na przeniesienie na konto (SRCH-30) bez zmiany formatu.
 * „Mojego wyszukiwania" z profilu to nie dotyka.
 */
const KLUCZ = 'flathero.historiaWyszukiwan.v1';
/** Stary zapis samych obszarów („Ostatnio szukane"), migrowany przy pierwszym odczycie. */
const KLUCZ_STARY = 'flathero.recentAreas.v1';
export const MAX_WPISOW = 5;

export interface WpisHistorii {
  /** Stan wyszukiwania w formacie adresu, bez widoku i kadru. To też klucz deduplikacji. */
  q: string;
  /** Kiedy zatwierdzono (ms) — pod przyszłe przeniesienie na konto i sortowanie. */
  t: number;
}

const POZA_WPISEM = ['view', 'm', 'oferta'];

/** Stan wyszukiwania w formacie adresu, znormalizowany (kolejność kluczy nie tworzy duplikatów). */
export function kluczWyszukiwania(f: SourcePostFilters): string {
  const p = filtersToParams(f);
  for (const k of POZA_WPISEM) p.delete(k);
  p.sort();
  return p.toString();
}

export function filtryZWpisu(w: WpisHistorii): SourcePostFilters {
  return paramsToFilters(new URLSearchParams(w.q));
}

function zapisz(wpisy: WpisHistorii[]) {
  try {
    window.localStorage.setItem(KLUCZ, JSON.stringify(wpisy));
  } catch {
    // Brak zapisu nie może przerwać wyszukiwania — człowiek traci tylko podpowiedź.
  }
}

/**
 * MIGRACJA: stare „Ostatnio szukane" to lista samych obszarów. Każdy obszar staje się wpisem
 * z samym obszarem — tak wyglądało wyszukiwanie, które wtedy zapisano. Stary klucz znika po
 * udanej migracji, żeby nie migrować drugi raz.
 */
function migrujStare(): WpisHistorii[] {
  try {
    const surowe = window.localStorage.getItem(KLUCZ_STARY);
    if (!surowe) return [];
    const tablica: unknown = JSON.parse(surowe);
    const wpisy: WpisHistorii[] = [];
    if (Array.isArray(tablica)) {
      for (const s of tablica) {
        const obszar = typeof s === 'string' ? parseArea(s) : null;
        if (!obszar) continue;
        const q = kluczWyszukiwania({ ...DEFAULT_FILTERS, areas: [obszar] });
        if (q && !wpisy.some((w) => w.q === q)) wpisy.push({ q, t: 0 });
      }
    }
    const wynik = wpisy.slice(0, MAX_WPISOW);
    zapisz(wynik);
    window.localStorage.removeItem(KLUCZ_STARY);
    return wynik;
  } catch {
    return [];
  }
}

export function czytajHistorie(): WpisHistorii[] {
  try {
    const surowe = window.localStorage.getItem(KLUCZ);
    if (surowe == null) return migrujStare();
    const tablica: unknown = JSON.parse(surowe);
    if (!Array.isArray(tablica)) return [];
    return tablica
      .filter((w): w is WpisHistorii => !!w && typeof (w as WpisHistorii).q === 'string' && (w as WpisHistorii).q !== '')
      .slice(0, MAX_WPISOW);
  } catch {
    return [];
  }
}

/** Zapis przy zatwierdzeniu. Pusty stan pomijamy; ten sam stan wędruje na górę. */
export function zapiszWyszukiwanie(f: SourcePostFilters, teraz = Date.now()): WpisHistorii[] {
  const q = kluczWyszukiwania(f);
  const obecne = czytajHistorie();
  if (!q) return obecne;
  const nowe = [{ q, t: teraz }, ...obecne.filter((w) => w.q !== q)].slice(0, MAX_WPISOW);
  zapisz(nowe);
  return nowe;
}

export function usunWpis(q: string): WpisHistorii[] {
  const nowe = czytajHistorie().filter((w) => w.q !== q);
  zapisz(nowe);
  return nowe;
}

export function wyczyscHistorie(): WpisHistorii[] {
  zapisz([]);
  return [];
}

/**
 * PODPIS WPISU: najważniejsze elementy („Mokotów · 2 pokoje · do 4 000 zł z opłatami"), reszta
 * jako „+N filtry". BEZ liczby ofert — ta się zmienia, a wpis mówi, czego szukałem.
 */
export function podpisWpisu(f: SourcePostFilters, t: TFunction, jezyk: string): { glowny: string; reszta: number } {
  const czesci: string[] = [];
  const nazwy = (f.areas ?? []).map((a) => etykietaObszaru(a, t));
  const dzielnice = f.suburbs ?? [];
  const miejsca = [...nazwy, ...dzielnice];
  if (miejsca.length) {
    czesci.push(miejsca.length > 2 ? `${miejsca.slice(0, 2).join(', ')} +${miejsca.length - 2}` : miejsca.join(', '));
  } else if (f.polygon?.length) {
    czesci.push(t('search.history.drawn_area'));
  } else {
    czesci.push(PRIMARY_ACTIVE_CITY);
  }
  const pokoje = etykietaWyboruPokoi(f.rooms, t);
  if (pokoje) czesci.push(pokoje);
  const cena = etykietaCenyZ(f, t as never, jezyk);
  if (cena) czesci.push([cena.zakres, cena.dopisek].filter(Boolean).join(' '));

  let reszta = 0;
  if (f.areaMin != null || f.areaMax != null) reszta += 1;
  if (f.nearMetro) reszta += 1;
  reszta += f.poi?.length ?? 0;
  if (f.postedSinceHours) reszta += 1;
  if (f.zeZwierzeciem) reszta += 1;
  if (f.availableNow) reszta += 1;
  for (const miekki of [f.umeblowanie, f.pietro, f.kuchnia, f.lazienki, f.balkon, f.winda, f.internet]) if (miekki) reszta += 1;
  if (f.kaucjaMax != null) reszta += 1;
  if (f.bezZdjec === false) reszta += 1;
  if (f.bezAdresu) reszta += 1;
  reszta += f.udogodnienia?.length ?? 0;
  if (f.polygon?.length && miejsca.length) reszta += 1;
  return { glowny: czesci.join(' · '), reszta };
}
